/**
 * 游戏内浮层(方案 UI2):替换 window.prompt/alert 三件套。
 * 与 HUD 同一视觉语言(深蓝底/金字/细边),ESC·遮罩·✕ 三种关闭。
 * 图鉴是转化率工具:每个神迹展示"触发口令"+已见/未见状态——教观众怎么玩。
 */
import { EFFECT_META } from '../effects/registry';
import { crc32 } from '../engine/util';
import { sfx } from '../engine/Sfx';
import type { World } from '../engine/World';
import type { WorldState } from '../engine/WorldState';
import type { WorldEvent } from '../types';

const ICON: Record<string, string> = { 轻: '⚡', 中: '✨', 大: '🌟', 戏剧: '🌗', 安全: '☁' };

/** 触发口令(观众语言,不是枚举名)。图鉴的核心价值。 */
const COMMAND: Record<string, string> = {
  rain: '发弹幕「我要下雨」', snow: '发弹幕「下雪」', wind: '发弹幕「起风」',
  fireworks: '发弹幕「放烟花」', rainbow: '发弹幕「彩虹」', meteor: '发弹幕「流星」或「许愿」',
  dance: '发弹幕「跳舞」', sky_color: '发弹幕「换个天」',
  aurora: '发弹幕「极光」', thunderstorm: '发弹幕「打雷」', day_night_flip: '发弹幕「天黑」/「天亮」',
  life_tide: '发弹幕「多点人」', whale: '发弹幕「巨鲸」',
  world_tree: '发弹幕「世界树」', statue: '发弹幕「神像」', meteor_shower: '单次投币 ≥50',
  epoch_leap: '香火抵达里程碑', sky_split: '发弹幕「撕裂天空」', dark_cloud: '说了不好听的话才会看到',
};

let overlay: HTMLElement | null = null;

export function closeModal() {
  overlay?.remove();
  overlay = null;
}

export function showModal(title: string, bodyHTML: string): HTMLElement {
  closeModal();
  overlay = document.createElement('div');
  overlay.className = 'lx-overlay';
  overlay.innerHTML = `
    <div class="lx-modal">
      <div class="lx-head"><span class="lx-title">${title}</span><button class="lx-close">✕</button></div>
      <div class="lx-body">${bodyHTML}</div>
    </div>`;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
  overlay.querySelector<HTMLButtonElement>('.lx-close')!.onclick = closeModal;
  const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { closeModal(); window.removeEventListener('keydown', esc); } };
  window.addEventListener('keydown', esc);
  document.body.appendChild(overlay);
  sfx.play('chime');
  return overlay.querySelector('.lx-body')!;
}

/** 神迹图鉴:口令 + 等级 + 已见/未见。已见=全服共享(服务端权威 seenTypes,持久化,人人一致、不重置)。 */
export function showCodex(world: World) {
  const seen = new Set(world.state.seenTypes);
  const types = Object.keys(EFFECT_META) as WorldEvent['type'][];
  const rows = types.map((t) => {
    const m = EFFECT_META[t];
    const ok = seen.has(t);
    return `<div class="cdx-row ${ok ? 'seen' : ''}">
      <span class="cdx-ic">${ICON[m.tier] ?? '·'}</span>
      <span class="cdx-name">${m.label}</span>
      <span class="cdx-tier t-${m.tier}">${m.tier}</span>
      <span class="cdx-cmd">${COMMAND[t] ?? ''}</span>
      <span class="cdx-state">${ok ? '已见证' : '未见过'}</span>
    </div>`;
  }).join('');
  showModal(`神迹图鉴 · ${seen.size}/${types.length} 已见证`, `<div class="cdx-list">${rows}</div>
    <div class="lx-hint">到 B 站视频发弹幕、投币、写评论,神谕将在这个世界真实生效。</div>`);
}

/** 认领我的神迹:UID → 神谕指纹 → 匹配历史。 */
export function showClaim(world: World) {
  const body = showModal('认领我的神迹', `
    <p class="lx-p">输入你的 B 站 UID,点亮本页见证过的、与该 UID 关联的神迹。</p>
    <div class="lx-row"><input id="claim-uid" class="lx-input" type="text" placeholder="B 站 UID" /><button id="claim-go" class="lx-btn">点亮</button></div>
    <div id="claim-result"></div>
    <div class="lx-hint">仅作展示与纪念;黄金纪「碑座刻名」等荣誉位最终以官方核验为准。</div>`);
  const input = body.querySelector<HTMLInputElement>('#claim-uid')!;
  const result = body.querySelector<HTMLElement>('#claim-result')!;
  const go = () => {
    const uid = input.value.trim();
    if (!uid) return;
    const midHash = crc32(uid);
    const mine = world.history.filter((h) => h.midHash === midHash);
    if (mine.length > 0) {
      const byType = new Map<string, number>();
      for (const m of mine) byType.set(m.type, (byType.get(m.type) ?? 0) + 1);
      const items = [...byType.entries()]
        .map(([t, n]) => `<div class="cdx-row seen"><span class="cdx-ic">${ICON[EFFECT_META[t as WorldEvent['type']]?.tier ?? '轻']}</span><span class="cdx-name">${EFFECT_META[t as WorldEvent['type']]?.label ?? t}</span><span class="cdx-state">×${n}</span></div>`)
        .join('');
      result.innerHTML = `<p class="lx-p gold">是你,唤醒了这些神迹:</p>${items}`;
      sfx.play('bell');
    } else {
      result.innerHTML = `<p class="lx-p">本期还没发现你的神迹。<br>发一条弹幕试试——下次它就会为你点亮。</p>`;
    }
  };
  body.querySelector<HTMLButtonElement>('#claim-go')!.onclick = go;
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  input.focus();
}

/** 香火账本:当前世界快照。 */
export function showLedger(state: WorldState) {
  const fmt = (n: number) => n.toLocaleString('en-US');
  const nx = state.next();
  showModal('香火账本', `
    <div class="lgr">
      <div class="lgr-row"><span>当前纪元</span><b>第${state.epochOrdinal}纪元 · ${state.epochName}</b></div>
      <div class="lgr-row"><span>累计香火</span><b class="gold">${fmt(state.faith)}</b></div>
      <div class="lgr-row"><span>下一里程碑</span><b>${nx ? `${fmt(nx.faith)}(${nx.name})` : '已至飞升纪'}</b></div>
      <div class="lgr-row"><span>烛民</span><b>${fmt(state.population)}</b></div>
      <div class="lgr-row"><span>圣诗碑</span><b>${fmt(state.stones.length)} 座</b></div>
    </div>
    <div class="lx-hint">正式运行后账本每 30 秒持久化,历史可查证。</div>`);
}
